import { useState } from "react";
import {
  BookOpen, Scroll, Users, Skull, MapPin, Flag, Castle, FileText,
  Sparkles, GitBranch, Clock, UserCircle2, Swords, ChevronRight, X, ExternalLink, Package,
} from "lucide-react";

// Campaign tools accessible from within the VTT.
// Clicking a tool opens a slide-out iframe panel so the DM can read Story/
// Sessions/Lore/etc. without leaving the tabletop.
const TOOLS = [
  { key: "chapters",      label: "Story",         icon: BookOpen,   path: "chapters" },
  { key: "sessions",      label: "Sessions",      icon: Scroll,     path: "sessions" },
  { key: "world-history", label: "World History", icon: Clock,      path: "world-history" },
  { key: "characters",    label: "Characters",    icon: UserCircle2, path: "characters" },
  { key: "npcs",          label: "NPCs",          icon: Users,      path: "npcs" },
  { key: "creatures",     label: "Bestiary",      icon: Skull,      path: "creatures" },
  { key: "locations",     label: "Locations",     icon: MapPin,     path: "locations" },
  { key: "maps",          label: "Maps",          icon: Castle,     path: "maps" },
  { key: "factions",      label: "Factions",      icon: Flag,       path: "factions" },
  { key: "lore",          label: "Lore",          icon: FileText,   path: "lore" },
  { key: "deities",       label: "Deities",       icon: Sparkles,   path: "deities" },
  { key: "items",         label: "Items",         icon: Package,    path: "items" },
  { key: "handouts",      label: "Handouts",      icon: FileText,   path: "handouts" },
  { key: "relationships", label: "Family Trees",  icon: GitBranch,  path: "relationships" },
  { key: "combat",        label: "Combat Tracker", icon: Swords,    path: "combat" },
];

export const VTTCampaignPanel = ({ campaignId }) => {
  const [active, setActive] = useState(null); // current tool key or null
  const activeTool = TOOLS.find((t) => t.key === active);
  const src = activeTool
    ? `/campaign/${campaignId}/${activeTool.path}?embedded=1`
    : null;

  return (
    <>
      {/* Vertical rail (always visible) */}
      <div
        className="absolute top-12 right-0 z-30 w-10 bg-[#1a1a1a]/95 border-l border-[#333] flex flex-col items-center py-2 gap-1"
        data-vtt-sidebar="tools-rail"
        data-testid="vtt-tools-rail"
        style={{ bottom: 40 }}
      >
        {TOOLS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              title={t.label}
              onClick={() => setActive(isActive ? null : t.key)}
              className={`w-8 h-8 rounded-sm flex items-center justify-center transition-colors ${
                isActive ? "bg-primary/25 text-primary" : "text-[#888] hover:text-white hover:bg-[#333]"
              }`}
              data-testid={`vtt-tool-${t.key}`}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}
      </div>

      {/* Slide-out iframe panel */}
      {activeTool && (
        <div
          className="absolute top-12 right-10 z-30 bg-[#1e1e1e] border-l border-[#333] flex flex-col shadow-2xl"
          style={{ bottom: 40, width: 480, animation: "slideInRight 0.25s ease-out" }}
          data-vtt-sidebar="tools-panel"
          data-testid={`vtt-panel-${activeTool.key}`}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#333] bg-[#222] flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <activeTool.icon className="w-4 h-4 text-primary flex-shrink-0" />
              <h3 className="text-sm font-medium text-white truncate">{activeTool.label}</h3>
            </div>
            <div className="flex items-center gap-1">
              <a
                href={`/campaign/${campaignId}/${activeTool.path}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#888] hover:text-white p-1"
                title="Open in new tab"
                data-testid={`vtt-panel-popout-${activeTool.key}`}
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button
                onClick={() => setActive(null)}
                className="text-[#888] hover:text-white p-1"
                data-testid="vtt-panel-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <iframe
            key={src}
            src={src}
            title={activeTool.label}
            className="flex-1 w-full border-0 bg-[#1a1a1a]"
            data-testid={`vtt-panel-iframe-${activeTool.key}`}
          />
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(40px); opacity: 0; }
          to   { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};
